import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, XCircle, Zap } from 'lucide-react';
import { AuthContext } from '../App';

/**
 * SubscriptionBanner - Shows warning banners based on subscription status
 * 
 * States:
 * - active: No banner shown
 * - past_due: Yellow warning - payment overdue, in grace period
 * - suspended: Red warning - account limited due to non-payment
 */
export default function SubscriptionBanner() {
  const { user } = useContext(AuthContext) || {};

  // Don't show banner if no user or no subscription info
  if (!user || !user.subscription_status) return null;

  const status = user.subscription_status;
  const daysUntilDue = user.days_until_due;
  const paymentDueDate = user.payment_due_date;

  // Active subscription - no banner needed
  if (status === 'active') {
    // Show warning if payment due in less than 7 days
    if (daysUntilDue !== null && daysUntilDue !== undefined && daysUntilDue <= 7 && daysUntilDue > 0) {
      return (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-blue-900 font-medium">
                  Payment due in {daysUntilDue} day{daysUntilDue !== 1 ? 's' : ''}
                </p>
                <p className="text-blue-700 text-sm">
                  Your subscription payment is due on {new Date(paymentDueDate).toLocaleDateString()}
                </p>
              </div>
            </div>
            <Link
              to="/admin/upgrade"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Zap className="w-4 h-4" />
              Renew Now
            </Link>
          </div>
        </div>
      );
    }
    return null;
  }

  // Past due (grace period) - yellow warning
  if (status === 'past_due') {
    const daysOverdue = daysUntilDue ? Math.abs(daysUntilDue) : 0;
    const daysLeft = Math.max(0, 7 - daysOverdue);

    return (
      <div className="bg-amber-50 border-b border-amber-300 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-amber-900 font-medium">
                Payment Overdue - {daysLeft} day{daysLeft !== 1 ? 's' : ''} left in grace period
              </p>
              <p className="text-amber-700 text-sm">
                You cannot add new items or categories. Make a payment to restore full access.
              </p>
            </div>
          </div>
          <Link
            to="/admin/upgrade"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Zap className="w-4 h-4" />
            Pay Now
          </Link>
        </div>
      </div>
    );
  }

  // Suspended - red warning
  if (status === 'suspended') {
    return (
      <div className="bg-red-50 border-b border-red-300 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-600" />
            <div>
              <p className="text-red-900 font-medium">
                Account Limited - Subscription Expired
              </p>
              <p className="text-red-700 text-sm">
                Your account is limited to Starter plan (50 items, 5 categories). Items beyond this limit are hidden from your public menu.
              </p>
            </div>
          </div>
          <Link
            to="/admin/upgrade"
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            <Zap className="w-4 h-4" />
            Reactivate
          </Link>
        </div>
      </div>
    );
  }

  return null;
}
